/**
 * Find in text (UX-MAP-15): the worker does the searching (`find` message); the reader keeps the returned character
 * ranges, washes them apart from the evidence marks, and steps through them. Nothing is searched on the main thread.
 */
import { useCallback, useMemo, useRef, useState } from 'preact/hooks';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { client } from '../worker-client.ts';
import type { FindRanges } from './Row.tsx';
import type { FindState } from './Toolbar.tsx';

const NONE: FindState = { query: '', total: 0, held: 0, current: -1, pending: false };

/** Index of the first range that starts at or after `pos` (ranges are flat [start, end, …] pairs, ascending). */
function firstFrom(ranges: Uint32Array, pos: number): number {
	let lo = 0;
	let hi = ranges.length / 2;
	while (lo < hi) {
		const mid = (lo + hi) >> 1;
		if ((ranges[mid * 2] as number) < pos) lo = mid + 1;
		else hi = mid;
	}
	return lo;
}

export function useFind(
	vm: ViewModel,
	paraOf: (pos: number) => number,
	/** Scroll the element matching a selector inside paragraph `p` into view. */
	reveal: (p: number, selector: string) => void,
	/** The paragraph at the reading line: the first match at or after it becomes current. */
	lineParagraph: () => number
): {
	find: FindState;
	findProp: FindRanges | null;
	findKey: string;
	runFind: (query: string) => void;
	stepFind: (dir: 1 | -1) => void;
} {
	const [find, setFind] = useState<FindState>(NONE);
	const ranges = useRef<Uint32Array>(new Uint32Array(0));
	const seq = useRef(0);

	const gotoMatch = useCallback(
		(i: number) => {
			const r = ranges.current;
			if (i < 0 || i * 2 >= r.length) return;
			reveal(paraOf(r[i * 2] as number), `.rd-find[data-find="${i}"]`);
		},
		[paraOf, reveal]
	);

	const runFind = useCallback(
		(query: string) => {
			const mine = ++seq.current;
			if (query.trim() === '') {
				ranges.current = new Uint32Array(0);
				setFind(NONE);
				return;
			}
			setFind((f) => ({ ...f, query, pending: true }));
			client
				.request({ type: 'find', id: client.id(), query, docHash: vm.meta.docHash }, ['found'])
				.then((res) => {
					if (mine !== seq.current) return;
					ranges.current = res.ranges;
					const held = res.ranges.length / 2;
					const from = vm.paras.charStart[lineParagraph()] as number;
					const cur = held === 0 ? -1 : Math.min(firstFrom(res.ranges, from), held - 1);
					setFind({ query, total: res.total, held, current: cur, pending: false });
					if (cur >= 0) requestAnimationFrame(() => gotoMatch(cur));
				})
				.catch(() => {
					if (mine === seq.current) setFind({ ...NONE, query });
				});
		},
		[vm, gotoMatch, lineParagraph]
	);

	const stepFind = useCallback(
		(dir: 1 | -1) => {
			setFind((f) => {
				if (f.held === 0) return f;
				const next = (f.current + dir + f.held) % f.held;
				requestAnimationFrame(() => gotoMatch(next));
				return { ...f, current: next };
			});
		},
		[gotoMatch]
	);

	const findProp = useMemo(
		() => (find.held > 0 ? { ranges: ranges.current, current: find.current } : null),
		[find.held, find.current]
	);
	const findKey = find.held > 0 ? `${find.query}:${find.current}:${find.held}` : '';
	return { find, findProp, findKey, runFind, stepFind };
}
